import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CopyRoutePayload,
  useRouteMutation,
  useRouteOrdersReports,
  useWorkspaceConnections,
} from "@web-app/api";
import { focusOnRoute } from "@web-app/pages/common/DispatchMap/mapFocusUtils";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, Route, User, Workspace } from "@web-app/types";

import { useAlert } from "../Alert/useAlert";

import {
  getOrders,
  getRouteStatusText,
  getScheduledStartAndEndDateTime,
} from "./utils";

export default function useRouteCard({
  assigneesMap,
  route,
  routedOrdersMap,
  isDefaultExpanded,
  searchTerm,
}: {
  assigneesMap?: Map<string, User>;
  isDefaultExpanded?: boolean;
  route: Route;
  routedOrdersMap: Map<string, Order>;
  searchTerm: string;
}) {
  const {
    focusedRouteId,
    dispatchMapRef,
    mapFocusedRouteId,
    user,
    expandedRouteId,
  } = useDispatchStore();
  const { showAlertAsync } = useAlert();
  const { data: workspaceConnections } = useWorkspaceConnections();
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    route.connectedWorkspaceId || "",
  );
  const { copyRoute, updateRouteV2 } = useRouteMutation();
  const hasReportsInRouteOrders = useRouteOrdersReports(
    route.ordersOrder.map((order) => order.id),
  );

  const prevSearchTermRef = useRef(searchTerm);

  const cachedIsExpanded = localStorage.getItem(
    `route-card-expanded-${route.id}`,
  );

  const [isExpanded, setIsExpandedState] = useState(
    typeof isDefaultExpanded === "boolean"
      ? isDefaultExpanded
      : cachedIsExpanded
        ? cachedIsExpanded === "true"
        : false,
  );

  const [shouldOpenUberModal, setShouldOpenUberModal] =
    useState<boolean>(false);

  const [shouldOpenRoadieModal, setShouldOpenRoadieModal] =
    useState<boolean>(false);

  const setIsExpanded = useCallback(
    (isExpanded: boolean) => {
      setIsExpandedState(isExpanded);
      localStorage.setItem(
        `route-card-expanded-${route.id}`,
        isExpanded.toString(),
      );
    },
    [route.id],
  );

  useEffect(() => {
    const hasSearchTermChanged = prevSearchTermRef.current !== searchTerm;
    prevSearchTermRef.current = searchTerm;

    if (searchTerm && hasSearchTermChanged) {
      const ordersArray = Array.from(routedOrdersMap.values());
      if (ordersArray.length > 0) {
        setIsExpanded(true);
      }
    } else if (!searchTerm && hasSearchTermChanged) {
      setIsExpanded(false);
    }
  }, [searchTerm, routedOrdersMap, setIsExpanded]);

  useEffect(() => {
    if (expandedRouteId !== route.id) {
      return;
    }

    if (!isExpanded) {
      setIsExpanded(true);
    }

    // Clear the expandedRouteId after expanding to avoid re-triggering
    useDispatchStore.setState({ expandedRouteId: null });
  }, [expandedRouteId, route.id, isExpanded, setIsExpanded]);

  const orders = getOrders(route, routedOrdersMap);

  const firstOrder = orders[0];
  const lastOrder = orders[orders.length - 1];

  const assignee = route.assigneeId
    ? assigneesMap?.get(route.assigneeId)
    : null;

  const [routeScheduledStartDateTime, routeScheduledEndDateTime] =
    getScheduledStartAndEndDateTime(firstOrder, lastOrder);

  const getTotalOrderCount = useMemo(() => {
    const routeOrders = route.ordersOrder.filter((routeOrder) =>
      routedOrdersMap.get(routeOrder.id),
    );
    return routeOrders.length;
  }, [route, routedOrdersMap]);

  const routeStatus = getRouteStatusText(route?.status, !!route.assigneeId);

  const handleClickCrosshair = () => {
    if (mapFocusedRouteId === route.id) {
      useDispatchStore.setState({ mapFocusedRouteId: null });
    } else {
      useDispatchStore.setState({
        focusedOrderId: null,
        mapFocusedOrderId: null,
        mapFocusedRouteId: route.id,
      });

      if (dispatchMapRef?.current) {
        focusOnRoute({
          mapRef: dispatchMapRef.current,
          orders: routedOrdersMap,
          route,
        });
      }
    }
  };

  const onClickEdit = () => {
    useDispatchStore.setState({
      focusedOrderId: null,
      focusedRouteId: route.id,
      mapFocusedRouteId: route.id,
    });

    // Focus on the route using the map instance directly
    if (dispatchMapRef?.current) {
      focusOnRoute({
        mapRef: dispatchMapRef.current,
        orders: routedOrdersMap,
        route,
      });
    }
  };

  const onRouteOrdersCardViewDetails = (orderId: string) => {
    useDispatchStore.setState({
      focusedOrderId: orderId,
    });
  };

  const routeTimezone = getOrders(route, routedOrdersMap)?.[0]?.timezone;

  const onCreateUberDelivery = (deliveryId: string) => {
    const uberRoute =
      useDispatchStore.getState().uberRouteIdAndDeliveryIdMap || {};
    useDispatchStore.setState((prev) => ({
      ...prev,
      uberRouteIdAndDeliveryIdMap: {
        ...uberRoute,
        [route.id]: deliveryId,
      },
    }));
  };

  const workspaceConnection = workspaceConnections?.find(
    (connection) =>
      connection.workspaceId === route.workspaceId ||
      connection.connectedWorkspaceId === route.workspaceId,
  );

  const workspace = workspaceConnection
    ? workspaceConnection.workspaceId === route.workspaceId
      ? workspaceConnection.workspace
      : workspaceConnection.connectedWorkspace
    : user?.currentWorkspace;

  let connectedWorkspace: Workspace | undefined;

  if (route?.connectedWorkspaceId) {
    const connectedWorkspaceConnection = workspaceConnections?.find(
      (connection) =>
        connection.workspaceId === route.connectedWorkspaceId ||
        connection.connectedWorkspaceId === route.connectedWorkspaceId,
    );
    connectedWorkspace = connectedWorkspaceConnection
      ? connectedWorkspaceConnection.connectedWorkspaceId ===
        route.connectedWorkspaceId
        ? connectedWorkspaceConnection.connectedWorkspace
        : connectedWorkspaceConnection.workspace
      : undefined;
  }

  const onShare = async (newConnectedWorkspaceId: string) => {
    await showAlertAsync(
      updateRouteV2.mutateAsync({
        connectedWorkspaceId: newConnectedWorkspaceId,
        ordersOrder: route.ordersOrder.map((order, index) => ({
          id: order.id,
          index,
          type: "order",
        })),
        routeId: route.id,
      }),
      {
        error: {
          message: "Something went wrong, please try again!",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Please wait",
          title: "Sending Route...",
          type: "default",
        },
        success: {
          message: "Successfully sent route",
          title: "Route Sent",
          type: "success",
        },
      },
    ).then(() => {
      setIsShareModalVisible(false);
    });
  };

  const handleCopyRoute = async (newDate: Date) => {
    const payload: CopyRoutePayload = {
      newScheduledDate: newDate.toISOString(),
    };

    const formattedDate = newDate.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    await showAlertAsync(
      copyRoute.mutateAsync({
        payload,
        routeId: route.id,
      }),
      {
        error: {
          message: "Something went wrong, please try again!",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Please wait...",
          title: "Copying Route...",
          type: "default",
        },
        success: {
          message: `Route copied successfully to ${formattedDate}`,
          title: "Success",
          type: "success",
        },
      },
    ).then(() => {
      setIsDuplicateModalOpen(false);
    });
  };

  return {
    assignee,
    connectedWorkspace,
    copyRoute,
    focusedRouteId,
    getTotalOrderCount,
    handleClickCrosshair,
    handleCopyRoute,
    hasReportsInRouteOrders,
    isCopyModalOpen: isDuplicateModalOpen,
    isExpanded,
    isShareModalVisible,
    lastOrder,
    mapFocusedRouteId,
    onClickEdit,
    onCreateUberDelivery,
    onRouteOrdersCardViewDetails,
    onShare,
    orders,
    routeScheduledEndDateTime,
    routeScheduledStartDateTime,
    routeStatus,
    routeTimezone,
    selectedWorkspaceId,
    setIsCopyModalOpen: setIsDuplicateModalOpen,
    setIsExpanded,
    setIsShareModalVisible,
    setSelectedWorkspaceId,
    setShouldOpenRoadieModal,
    setShouldOpenUberModal,
    shouldOpenRoadieModal,
    shouldOpenUberModal,
    status: route.status,
    workspace,
    workspaceConnections,
  };
}
